import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { StageData, Workflow, WorkflowCreationData, WorkflowStage } from '@/components/ApprovalStageWizard/types';
import type { BulkAssignWorkflowRequest } from '@/types/workflow-assignment';
import type { Employee } from '@/types/timesheet';
import { ApiError } from '@/services/apiClient';
import * as workflowService from '@/services/workflowService';
import { bulkAssignWorkflow } from '@/services/workflowAssignmentService';
import { useWorkflowCreation } from './useWorkflowCreation';
import { useWorkflowStageEditor } from './useWorkflowStageEditor';
import { useWorkflowStageMutations } from './useWorkflowStageMutations';

export interface UseWorkflowActionsParams {
  isLive: boolean;
  selectedWorkflowId: string | null;
  workflows: Workflow[];
  setWorkflows: React.Dispatch<React.SetStateAction<Workflow[]>>;
  employees: Employee[];
  loadWorkflows: () => Promise<void>;
  loadEmployees: () => Promise<void>;
  /** Called with the new workflow id once creation succeeds. */
  onWorkflowCreated: (workflowId: string) => void;
  /** Called once a workflow has been deleted successfully. */
  onWorkflowDeleted: (workflowId: string) => void;
  /** Called when a delete attempt finishes, whether it succeeded or failed. */
  onDeleteSettled: () => void;
  /** Called once a duplication attempt finishes successfully. */
  onWorkflowDuplicated: () => void;
}

export interface UseWorkflowActionsResult {
  handleCreateWorkflow: (data: WorkflowCreationData, selectedTemplate: string | null) => Promise<void>;
  handleUpdateStage: (stageId: string, stageData: StageData) => Promise<void>;
  handleSaveDraft: (stageId: string, stageData: StageData) => Promise<void>;
  handleDeleteStage: (stageId: string) => Promise<void>;
  handleReorderStages: (reorderedStages: WorkflowStage[]) => Promise<void>;
  handleDuplicateStageInWorkflow: (stageId: string) => Promise<void>;
  handleUpdateWorkflowStatus: (workflowId: string, newStatus: Workflow['status']) => Promise<void>;
  handleDeleteWorkflow: (workflowId: string) => Promise<void>;
  handleDuplicate: (
    workflowId: string,
    newName: string,
    options: { copyStages: boolean; copyRules: boolean; copyAssignments: boolean }
  ) => Promise<void>;
  handleAssign: (
    workflowId: string,
    assignmentType: 'all' | 'selected',
    selectedEmployeeIds?: number[]
  ) => Promise<void>;
  isDeleting: boolean;
}

/**
 * Aggregates every workflow-level and stage-level action for the approval
 * workflows page.
 */
export const useWorkflowActions = ({
  isLive,
  selectedWorkflowId,
  workflows,
  setWorkflows,
  employees,
  loadWorkflows,
  loadEmployees,
  onWorkflowCreated,
  onWorkflowDeleted,
  onDeleteSettled,
  onWorkflowDuplicated,
}: UseWorkflowActionsParams): UseWorkflowActionsResult => {
  const [isDeleting, setIsDeleting] = useState(false);

  const { handleCreateWorkflow, handleDuplicate } = useWorkflowCreation({
    isLive,
    workflows,
    setWorkflows,
    loadWorkflows,
    onWorkflowCreated,
    onWorkflowDuplicated,
  });

  const { handleUpdateStage, handleSaveDraft } = useWorkflowStageEditor({
    isLive,
    selectedWorkflowId,
    workflows,
    setWorkflows,
    loadWorkflows,
  });

  const { handleDeleteStage, handleReorderStages, handleDuplicateStageInWorkflow } =
    useWorkflowStageMutations({
      isLive,
      selectedWorkflowId,
      workflows,
      setWorkflows,
      loadWorkflows,
    });

  const handleUpdateWorkflowStatus = useCallback(async (
    workflowId: string,
    newStatus: Workflow['status']
  ) => {
    // Update local state first for immediate feedback
    setWorkflows((prev) =>
      prev.map((workflow) => {
        if (workflow.id !== workflowId) return workflow;

        return {
          ...workflow,
          status: newStatus,
          updatedAt: new Date(),
        };
      })
    );

    // Persist to backend in live mode
    if (isLive) {
      try {
        if (newStatus === 'active') {
          await workflowService.activateWorkflow(workflowId);
          toast.success('Workflow activated');
        } else if (newStatus === 'inactive' || newStatus === 'draft') {
          // Backend only supports isActive boolean, so both 'inactive' and 'draft' map to isActive=false
          const { unassignedCount } = await workflowService.deactivateWorkflow(workflowId);
          if (unassignedCount > 0) {
            toast.success(
              `Workflow deactivated. ${unassignedCount} employee${unassignedCount !== 1 ? 's' : ''} reverted to the default workflow.`
            );
          } else {
            toast.success(newStatus === 'draft' ? 'Workflow set to draft' : 'Workflow deactivated');
          }
        }
        // Refresh to reflect updated assignment counts
        loadWorkflows();
      } catch (err) {
        console.error('Failed to update workflow status:', err);
        if (err instanceof ApiError) {
          toast.error(err.message);
        } else {
          toast.error('Failed to update workflow status');
        }
        // Revert local state on error
        loadWorkflows();
      }
    }
  }, [isLive, setWorkflows, loadWorkflows]);

  const handleDeleteWorkflow = useCallback(async (workflowId: string) => {
    setIsDeleting(true);
    try {
      if (isLive) {
        await workflowService.deleteWorkflow(workflowId);
      }
      setWorkflows((prev) => prev.filter((w) => w.id !== workflowId));
      // If viewing the deleted workflow, go back to list
      onWorkflowDeleted(workflowId);
      toast.success('Workflow deleted successfully');
    } catch (err) {
      console.error('Failed to delete workflow:', err);
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error('Failed to delete workflow');
      }
    } finally {
      setIsDeleting(false);
      onDeleteSettled();
    }
  }, [isLive, setWorkflows, onWorkflowDeleted, onDeleteSettled]);

  const handleAssign = useCallback(async (
    workflowId: string,
    assignmentType: 'all' | 'selected',
    selectedEmployeeIds?: number[]
  ) => {
    try {
      if (isLive) {
        // Use bulkAssignWorkflow from workflowAssignmentService in live mode
        const employeeIdsToAssign = assignmentType === 'all'
          ? employees.map(e => e.id)
          : selectedEmployeeIds || [];

        if (employeeIdsToAssign.length === 0) {
          toast.error('No employees selected for assignment');
          return;
        }

        const request: BulkAssignWorkflowRequest = {
          employeeIds: employeeIdsToAssign,
          workflowId: workflowId,
          reason: 'Assigned via Approval Workflows page',
        };

        const result = await bulkAssignWorkflow(request);

        // Show success toast if at least one assignment succeeded
        // This handles the case where some assignments succeed but others fail
        if (result.successCount > 0) {
          toast.success(`Successfully assigned workflow to ${result.successCount} employee(s)`);
          if (result.failedCount > 0) {
            toast.warning(`${result.failedCount} assignment(s) failed`);
          }
          // Reload employees to update assignment counts in UI
          await loadEmployees();
        } else {
          // Only show error if ALL assignments failed
          toast.error(result.message || 'Failed to assign workflow');
        }
      } else {
        // Demo mode - use mock assignment
        const result = await workflowService.assignWorkflow(workflowId, assignmentType, selectedEmployeeIds);
        toast.success(`Successfully assigned workflow to ${result.assignedCount} employee(s)`);
      }
    } catch (error) {
      console.error('Failed to assign workflow:', error);
      toast.error('Failed to assign workflow. Please try again.');
    }
  }, [isLive, employees, loadEmployees]);

  return {
    handleCreateWorkflow,
    handleUpdateStage,
    handleSaveDraft,
    handleDeleteStage,
    handleReorderStages,
    handleDuplicateStageInWorkflow,
    handleUpdateWorkflowStatus,
    handleDeleteWorkflow,
    handleDuplicate,
    handleAssign,
    isDeleting,
  };
};
